import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Checkbox,
  Grid,
  InputLabel,
  Paper,
  IconButton,
} from '@mui/material';
import { AppState } from '../../../store';
import EnhancedTable from '../../table-old';
import { HeadCell } from '../../table-old/components/expandable-row';
import DeleteIcon from '../../../assets/icons/delete-icon';
import useStyles from './styles';
import {
  getSFTPUser,
  getAllSFTPUsers,
  clearSFTPState,
  patchDealer,
} from '../../../store/sftp/actions';
import SimpleTableCustomCell from '../../simple-table-custom-cell';
import RuleCheckBox from './rule-check-box';
import AcceptIcon from '../../../assets/icons/accept-icon';
import CancelIcon from '../../../assets/icons/cancel-icon';

const ManageDealers = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [dealerCode, setDealerCode] = useState<string>('');
  const [isSearched, setSearched] = useState<boolean>(false);
  const [selectedEmailIds, setSelectedEmailIds] = useState<any>([]);
  const dealerInfo = useSelector((state: AppState) => state.sftp.dealerInfo);
  const isSftpUserNotReportingAlertsEnabled = useSelector(
    (state: AppState) => state.sftp.isSftpUserNotReportingAlertsEnabled,
  );
  const [alertsEnabled, setAlertsEnabled] = useState<boolean>(false);
  const emails = useSelector((state: AppState) => state.sftp.emails);
  const dealerRules = useSelector((state: AppState) => state.sftp.rules);
  const allDealers = useSelector((state: AppState) => state.sftp.allDealerCodes);
  const [rules, setRules] = useState<any>([]);
  const [newEmail, setNewEmail] = useState<string>('');
  const [showConfirmFlag, setShowConfirmFlag] = useState<boolean>(false);
  const emailRegex =
    /^(([^\s"(),.:;<>@[\]]+(\.[^\s"(),.:;<>@[\]]+)*)|(".+"))@(([^\s"(),.:;<>@[\]]+\.)+[^\s"(),.:;<>@[\]]{2,})$/i;

  const clearState = () => {
    setDealerCode('');
    setRules([]);
    setNewEmail('');
    dispatch(clearSFTPState());
    setSearched(false);
  };
  useEffect(() => {
    dispatch(getAllSFTPUsers());
    clearState();
  }, []);
  useEffect(() => {
    setRules(dealerRules);
    setAlertsEnabled(isSftpUserNotReportingAlertsEnabled);
    setNewEmail('');
  }, [dealerInfo]);

  const headCellsEmail: HeadCell[] = [
    {
      id: 'email',
      numeric: false,
      disablePadding: true,
      label: 'Email',
      accessor: 'row.email',
    },
  ];
  const toggleRule = (row: any) => {
    const newRules = JSON.parse(JSON.stringify(rules));
    const index = newRules.findIndex((x: any) => x.id === row.id);
    if (index >= 0) {
      newRules[index].isEnabled = !row.isEnabled;
      setRules(newRules);
    }
  };
  const rulesChanged = () => JSON.stringify(rules) === JSON.stringify(dealerRules);
  const headCellsRules = [
    {
      id: 'Enabled',
      label: 'Active',
      accessor: 'isEnabled',
      customCellComponent: RuleCheckBox,
      callback: toggleRule,
    },
    {
      id: 'rules',
      label: 'Rules',
      accessor: 'rule',
    },
  ];
  const removeEmail = () => {
    const remainingEmails = emails.filter((email) => !selectedEmailIds.includes(email.id));
    dispatch(
      patchDealer(dealerCode, {
        associatedEmailAddresses: remainingEmails.map((emailObject: any) => emailObject.email),
      }),
    );
  };
  const disableSearch = () => {
    if (dealerCode && dealerCode.length > 0) return false;
    return true;
  }
  return (
    <>
      <Grid container direction="row" alignItems="stretch">
        <Grid item xs={12} lg={6} className={classes.dealerSearchContainer}>
          <Box p={3} className={classes.box}>
            <InputLabel className={classes.label}>Dealer Code</InputLabel>
            <Autocomplete
              id="dealerSearch"
              options={allDealers}
              className={classes.dropdown}
              renderInput={(parameters) => <TextField variant="standard" {...parameters} />}
              onChange={(event, newValue: string | null) => {
                if (newValue) {
                  setDealerCode(`${newValue}`);
                } else {
                  clearState();
                }
              }}
            />
            <Button
              disabled={disableSearch()}
              variant="outlined"
              onClick={() => {
                dispatch(getSFTPUser(dealerCode));
                setSearched(true);
              }}
              className={classes.button}
            >
              Search
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} lg={3} />
        <Grid
          item
          xs={12}
          lg={3}
          className={isSearched ? classes.dealerTableSection : classes.invisible}
        >
          <Box p={3} className={classes.box} style={{ float: 'right', width: '100%' }}>
            {!showConfirmFlag ? (
              <>
                <InputLabel className={classes.alertLabel}>Users Not Reporting Alerts</InputLabel>
                <Checkbox checked={alertsEnabled} onClick={() => setShowConfirmFlag(true)} />
              </>
            ) : (
              <>
                <InputLabel className={classes.alertLabel}>Are you sure?</InputLabel>
                <Box style={{ width: '25%' }}>
                  <IconButton
                    id="confirm-flag"
                    onClick={() => {
                      dispatch(
                        patchDealer(dealerCode, {
                          isSftpUserNotReportingAlertsEnabled: !alertsEnabled,
                        }),
                      );
                      setShowConfirmFlag(false);
                    }}
                    size="large"
                  >
                    <AcceptIcon />
                  </IconButton>
                  <IconButton
                    id="deny"
                    onClick={() => setShowConfirmFlag(false)}
                    aria-label="edit"
                    size="large"
                  >
                    <CancelIcon />
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          lg={6}
          className={isSearched ? classes.dealerTableSection : classes.invisible}
        >
          <Box p={3} className={classes.box}>
            <InputLabel className={classes.label}>Email</InputLabel>
            <TextField
              className={classes.textBox}
              variant="standard"
              value={newEmail}
              onChange={(event) => {
                setNewEmail(event.target.value as string);
                return event.target.value;
              }}
            />
            <Button
              variant="outlined"
              className={classes.button}
              disabled={!emailRegex.test(newEmail)}
              onClick={() => {
                dispatch(
                  patchDealer(dealerCode, {
                    associatedEmailAddresses: [
                      ...emails.map((emailObject: any) => emailObject.email),
                      newEmail,
                    ],
                  }),
                );
              }}
            >
              Add
            </Button>
          </Box>
          <Box>
            {selectedEmailIds.length > 0 && emails.length > 0 && (
              <IconButton
                style={{ float: 'left', width: '5%' }}
                id="deleteButton"
                onClick={() => removeEmail()}
                aria-label="delete"
                className={classes.delete}
              >
                <DeleteIcon />
              </IconButton>
            )}
            <Paper style={{ marginRight: '0px', marginLeft: '0px', width: '92%', float: 'right' }}>
              <EnhancedTable
                initialSort="modifiedDateTime"
                id="dealerEmailsTable"
                rows={emails}
                headCells={headCellsEmail}
                expandableData={[]}
                selectableRows={(selection: any) => {
                  setSelectedEmailIds(selection);
                }}
              />
            </Paper>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          lg={6}
          className={rules.length > 0 ? classes.dealerTableSection : classes.invisible}
        >
          <Box p={3} className={classes.box} style={{ float: 'right', marginRight: '0' }}>
            <Button
              variant="outlined"
              disabled={rulesChanged()}
              onClick={() => {
                dispatch(
                  patchDealer(dealerCode, {
                    validationRules: rules.map((rule: any) => ({
                      rule: rule.rule,
                      isEnabled: rule.isEnabled,
                    })),
                  }),
                );
              }}
            >
              Update Rules
            </Button>
          </Box>
          <Paper style={{ marginLeft: '6%', width: '92%', float: 'left' }}>
            <SimpleTableCustomCell
              initialSort="modifiedDateTime"
              id="dealerRulesTable"
              rows={rules}
              headCells={headCellsRules}
              maxHeight={600}
            />
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default ManageDealers;
